import { UseFormReturn } from "react-hook-form";
import { CheckIcon, ChevronDown, Code2Icon, Search, Wand2, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import CountryFlag from 'react-country-flag';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { CampaignFormData } from "@/components/dashboard_create_campaign/campaignFormTypes";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const VITE_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;

const FOLLOWER_RANGES = {
  FROM: [
    { value: "0", label: "min" },
    { value: "10000", label: "10k" },
    { value: "25000", label: "25k" },
    { value: "100000", label: "100k" },
    { value: "300000", label: "300k" },
    { value: "1000000", label: "1M" },
  ],
  TO: [
    { value: "10000", label: "10k" },
    { value: "25000", label: "25k" },
    { value: "100000", label: "100k" },
    { value: "300000", label: "300k" },
    { value: "1000000", label: "1M" },
    { value: "1000000000", label: "max" },
  ],
};

export const COUNTRIES = [
  { value: "US", label: "United States", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "CA", label: "Canada", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "GB", label: "United Kingdom", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "IT", label: "Italy", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "DE", label: "Germany", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "FR", label: "France", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "ES", label: "Spain", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "SE", label: "Sweden", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "NL", label: "Netherlands", disabled: false, platforms: ["tiktok","youtube", "instagram"] },
  { value: "PL", label: "Poland", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "BE", label: "Belgium", disabled: false, platforms: ["tiktok", "instagram"] },
  { value: "IE", label: "Ireland", disabled: false, platforms: ["tiktok", "instagram"] },
  { value: "CH", label: "Switzerland", disabled: false, platforms: ["tiktok", "instagram"] },
  { value: "MX", label: "Mexico", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "BR", label: "Brazil", disabled: false, platforms: ["instagram"] },
  { value: "UY", label: "Uruguay", disabled: false, platforms: ["instagram"] },
  { value: "CL", label: "Chile", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "SV", label: "El Salvador", disabled: false, platforms: ["instagram"] },
  { value: "PA", label: "Panama", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "PE", label: "Peru", disabled: false, platforms: ["youtube"] },
  { value: "AR", label: "Argentina", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "CR", label: "Costa Rica", disabled: false, platforms: ["instagram"] },
  { value: "CO", label: "Colombia", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "EC", label: "Ecuador", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "VE", label: "Venezuela", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "BO", label: "Bolivia", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "AE", label: "United Arab Emirates", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "SA", label: "Saudi Arabia", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "QA", label: "Qatar", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "BH", label: "Bahrain", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "OM", label: "Oman", disabled: false, platforms: ["tiktok", "instagram",  "youtube"] },
  { value: "KW", label: "Kuwait", disabled: false, platforms: ["tiktok", "instagram", "youtube"] },
  { value: "JO", label: "Jordan", disabled: false, platforms: ["instagram"] },
  { value: "LB", label: "Lebanon", disabled: false, platforms: ["instagram"] },
  { value: "AM", label: "Armenia", disabled: false, platforms: ["instagram"] },
  { value: "BY", label: "Belarus", disabled: false, platforms: ["instagram"] },
  { value: "GE", label: "Georgia", disabled: false, platforms: ["instagram"] },
  { value: "KZ", label: "Kazakhstan", disabled: false, platforms: ["instagram"] },
  { value: "KG", label: "Kyrgyzstan", disabled: false, platforms: ["instagram"] },
  { value: "MD", label: "Moldova", disabled: false, platforms: ["instagram"] },
  { value: "RU", label: "Russia", disabled: false, platforms: ["instagram"] },
  { value: "TJ", label: "Tajikistan", disabled: false, platforms: ["instagram"] },
  { value: "TM", label: "Turkmenistan", disabled: false, platforms: ["instagram"] },
  { value: "UA", label: "Ukraine", disabled: false, platforms: ["instagram"] },
  { value: "AZ", label: "Azerbaijan", disabled: false, platforms: ["instagram"] },
  { value: "UZ", label: "Uzbekistan", disabled: false, platforms: ["instagram"] },
  { value: "IL", label: "Israel", disabled: false, platforms: ["instagram"] },
  { value: "HK", label: "Hong Kong", disabled: false, platforms: ["tiktok", "youtube", "instagram"] },
  { value: "CN", label: "China", disabled: false, platforms: ["instagram", "youtube"] },
  { value: "MY", label: "Malaysia", disabled: false, platforms: ["tiktok", "youtube"] },
  { value: "TW", label: "Taiwan", disabled: false, platforms: ["youtube", "instagram"] },
  { value: "GR", label: "Greece", disabled: true, platforms: ["tiktok", "instagram"] },
  { value: "VN", label: "Vietnam", disabled: false, platforms: ["tiktok"] },
  { value: "TH", label: "Thailand", disabled: false, platforms: ["tiktok"] },
  { value: "ID", label: "Indonesia", disabled: false, platforms: ["tiktok"] },
  { value: "SG", label: "Singapore", disabled: false, platforms: ["tiktok"] },
  { value: "KR", label: "South Korea", disabled: false, platforms: ["tiktok"] },
  { value: "AU", label: "Australia", disabled: false, platforms: ["tiktok","instagram", "youtube"] },
  { value: "PK", label: "Pakistan", disabled: false, platforms: ["youtube"] },
  { value: "BD", label: "Bangladesh", disabled: false, platforms: ["youtube"] },
];

interface CampaignFormStep2Props {
  form: UseFormReturn<CampaignFormData>;
  usage: any;
}

export default function CampaignFormStep2({ form, usage }: CampaignFormStep2Props) {
  const { user } = useUser();
  // Staff-only extras (Clerk publicMetadata uplodio_member) — the server
  // re-verifies the flag, this only gates the UI.
  const isStaff = Boolean(
    (user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member
  );
  const examples = [
    "Fitness coaches sharing home workout routines",
    "A 30 year old woman that does a 'get ready with me' to advertise a lipstick",
    "Fashion influencers who creates content about sustainable brands",
    "The new Ford Mustang to be advertised from a 30 year old man",
    "Food bloggers focusing on street food and local cuisine",
    "Tech reviewers specializing in budget gadgets",
  ];
  const [exampleIndex, setExampleIndex] = useState(0);
  const selectedPlatform = form.watch("platform");

  // Clear countries when platform changes
  useEffect(() => {
    const currentCountries = form.getValues("countries") || [];
    const availableCountries = COUNTRIES.filter(country =>
      country.platforms.includes(selectedPlatform)
    ).map(country => country.value);

    // Filter out countries that are no longer available for the selected platform
    const validCountries = currentCountries.filter(country =>
      availableCountries.includes(country)
    );

    if (validCountries.length !== currentCountries.length) {
      form.setValue("countries", validCountries);
    }
  }, [selectedPlatform, form]);

  function tryNextExample() {
    const next = (exampleIndex + 1) % examples?.length;
    form.setValue("creator_prompt", examples[next]);
    setExampleIndex(next);
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="follower_range_from"
          render={({ field }) => (
            <FormItem className="flex flex-col relative mb-6">
              <FormLabel>Minimum Followers</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(Number(value));
                }}
                defaultValue={field.value?.toString()}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select minimum" />
                </SelectTrigger>
                <SelectContent>
                  {FOLLOWER_RANGES.FROM.map((range) => (
                    <SelectItem
                      key={range.value}
                      value={range.value.toString()}
                    >
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="follower_range_to"
          render={({ field }) => (
            <FormItem className="flex flex-col relative mb-6">
              <FormLabel>Maximum Followers</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(Number(value));
                }}
                defaultValue={field.value?.toString()}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select maximum" />
                </SelectTrigger>
                <SelectContent>
                  {FOLLOWER_RANGES.TO.map((range) => (
                    <SelectItem
                      key={range.value}
                      value={range.value.toString()}
                    >
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="number_of_creators"
          render={({ field }) => (
            <FormItem className="relative mb-6">
              <FormLabel>Number of Creators</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={10}
                  max={1000}
                  disabled={usage?.plan === "free"}
                  placeholder="Enter number of creators"
                  {...field}
                  onChange={(e) => {
                    const value = Number.parseInt(e.target.value);
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormDescription>
                {usage?.plan === "free" ? (
                  <span className="text-destructive text-xs">
                    Free plan only support 10 creators
                  </span>
                ) : (
                  <span>Choose between 10 and 1000 creators.</span>
                )}
              </FormDescription>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="countries"
          render={({ field }) => {
            const [searchTerm, setSearchTerm] = useState("")
            const selectedPlatform = form.watch("platform")

            const getDisplayText = () => {
              if (field.value?.length === 0) return "Select countries"
              if (field.value?.length <= 2) {
                const selectedCountries = field.value.map(value =>
                  COUNTRIES.find(c => c.value === value)?.label || value
                )
                return selectedCountries.join(", ")
              }
              return `${field.value?.length} countries selected`
            }

            const availableCountries = COUNTRIES.filter(country =>
              country.platforms.includes(selectedPlatform)
            )

            const filteredCountries = availableCountries.filter(country =>
              country.label.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
              country.value.toLowerCase()?.includes(searchTerm.toLowerCase())
            )

            const selectCountry = (country: string) => {
              if (field.value?.includes(country)) {
                field.onChange(field.value.filter(c => c !== country))
              } else {
                field.onChange([...field.value, country])
              }
            }

            return (
              <FormItem className="flex flex-col gap-2 relative mb-6">
                <FormLabel className="flex flex-row items-center justify-between">Countries
                  <span className="text-xs text-muted-foreground h-3">
                    Select Multiple
                  </span>
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="justify-between text-sm font-normal"
                      type="button"
                    >
                      {getDisplayText()}
                      <ChevronDown className="opacity-30" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-74 p-0 rounded-md divide-y divide-border flex flex-col gap-1">
                    <div className="relative">
                      <Search className="absolute left-2.25 top-2.75 size-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search countries . . ."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-10 border-none shadow-none focus-visible:ring-0 ring-0 md:text-xs focus-visible:border-none bg-popover"
                        autoFocus
                      />
                      {searchTerm && <div className="absolute right-1.5 top-1.5">
                        <Button
                          type="button"
                          variant="muted-ghost"
                          size="icon-xs"
                          scope={false}
                          onClick={() => setSearchTerm("")}
                        >
                          <XIcon />
                        </Button>
                      </div>}
                    </div>
                    <div className="flex flex-col gap-0 px-1 max-h-68 overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent] scrollbar-thin">
                      {filteredCountries.map((country) => (
                        <Button
                          type="button"
                          key={country.value}
                          size="sm"
                          className={`text-start justify-start w-full transition-none shrink-0 rounded-xs ${country.disabled ? "opacity-60" : ""}`}
                          variant="ghost"
                          scope={false}
                          disabled={country.disabled}
                          onClick={() => selectCountry(country.value)}
                        >
                          <CountryFlag
                            svg
                            className="rounded-[min(var(--radius-xs),2px)] min-w-3.5"
                            countryCode={country.value}
                            title={country.label}
                          />
                          {country.label}
                          {field.value?.includes(country.value as string) && (
                            <CheckIcon className="ml-auto animate-in fade-in zoom-in duration-50" />
                          )}
                          {country.disabled && (
                            <span className="text-xs italic ml-auto">
                              Coming Soon
                            </span>
                          )}
                        </Button>
                      ))}
                      {filteredCountries?.length === 0 && (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          No countries found
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage className="text-xs" />
              </FormItem>
            )
          }}
        />
      </div>

      <FormField
        control={form.control}
        name="creator_prompt"
        render={({ field }) => (
          <FormItem className="relative">
            <FormLabel>Ideal Creator Description</FormLabel>
            <div className="relative w-full">
              <FormControl>
                <Textarea
                  placeholder="Describe your ideal creator (e.g., fashion influencers who create authentic content about sustainable brands)"
                  className="resize-none min-h-32 pr-32 box-border"
                  autoFocus
                  {...field}
                />
              </FormControl>
              {VITE_ENVIRONMENT != "production" && <Button
                type="button"
                variant="secondary"
                size="sm"
                className="absolute bottom-2 right-34"
                onClick={() => { field.onChange("dev!"); }}
              >
                <Code2Icon /> Add Dev Prompt
              </Button>}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="absolute bottom-2 right-2"
                onClick={tryNextExample}
              >
                <Wand2 /> Try Example
              </Button>
            </div>
            <FormDescription>
              Describe the type of creator you're looking for. Click
              "Try Example" to cycle sample prompts.
            </FormDescription>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      {isStaff && (
        <FormField
          control={form.control}
          name="batch_name"
          render={({ field }) => (
            <FormItem className="relative mt-6">
              <FormLabel className="flex flex-row items-center justify-between">
                Sourcing batch
                <span className="text-xs text-muted-foreground h-3">
                  Staff only
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="freddies_insta_uk_8, zaro_insta_us_2, etc."
                  className="font-mono"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormDescription>
                Restricts the creator search to these raw_creators batches
                (comma-separated for several). An unknown batch matches
                nobody. Leave empty to search the whole corpus.
              </FormDescription>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}